import React, { useEffect, ReactNode } from 'react';
import posthog from 'posthog-js';
import { useSessionStore } from '../../../state/src/useSessionStore';
import type { SessionState } from '../../../state/src/useSessionStore';
import { initializeFetchInterceptor } from '../utils/fetchInterceptor';
import { setupInputValueCapture } from '../utils/inputCapture';
import { getPostHogConfig } from '../config';

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  

  useEffect(() => {
    // Get PostHog configuration from config files
    const config = getPostHogConfig();

    // Only initialize PostHog if we have an API key
    if (config.apiKey && typeof window !== 'undefined') {
      posthog.init(config.apiKey, {
        api_host: config.apiHost,
        debug: true, //set to true to see logs in browser console,
        capture_pageview: true,
        capture_pageleave: true,
        capture_performance: true,
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['button', 'input', 'select'],
          css_selector_allowlist: ['button', 'input[type="radio"]', 'select']
        },
        loaded: (posthog: any) => {
          // Set global PostHog instance for input capture
          if (typeof window !== 'undefined') {
            window.posthog = posthog;
          }
          
          initializeFetchInterceptor();
          setupInputValueCapture();
        }
      });
    }
  }, []);

  // Separate effect to handle user identification when kioskId changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.posthog && kioskId) {
      if (agentId && unitProfileId) {
        window.posthog.identify(kioskId, {
          'data-mg-qrcode-number': kioskId,
          'data-mg-kiosk-profile-id': unitProfileId,
          'data-mg-agent-id': agentId,
          'data-mg-kiosk-id': kioskId,
        });
        
        window.posthog.capture('qr_code_scanned', {
          'data-mg-qrcode-number': kioskId,
          'data-mg-kiosk-profile-id': unitProfileId,
          'data-mg-agent-id': agentId,
          'data-mg-kiosk-id': kioskId,
        });
      } else {
        window.posthog.identify(kioskId, { 'data-mg-qrcode-number': kioskId });
        window.posthog.capture('qr_code_scanned', { 'data-mg-qrcode-number': kioskId });
      }
    }
  }, [kioskId, agentId, unitProfileId]);

  return <>{children}</>;
};